import { useEffect, useMemo, useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, Search } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';

interface HuduPasswordMeta {
  id: number;
  name: string;
  username: string | null;
  url: string | null;
  description: string | null;
  passwordableType: string | null;
  passwordableId: number | null;
}

interface HuduPasswordReveal extends HuduPasswordMeta {
  password: string;
}

interface Props {
  organizationId: string;
  organizationName: string;
  onClose: () => void;
}

export function HuduPasswordModal({ organizationId, organizationName, onClose }: Props): React.ReactElement {
  const [passwords, setPasswords] = useState<HuduPasswordMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // password id → revealed plaintext, kept locally so each click logs once.
  const [revealed, setRevealed] = useState<Record<number, string>>({});
  const [revealingId, setRevealingId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  useEffect(() => {
    apiFetch<{ passwords: HuduPasswordMeta[] }>(
      `/api/hudu/passwords?organizationId=${encodeURIComponent(organizationId)}`,
    )
      .then((r) => setPasswords(r.passwords))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load passwords.'));
  }, [organizationId]);

  const filtered = useMemo(() => {
    if (!passwords) return null;
    const q = search.trim().toLowerCase();
    if (!q) return passwords;
    return passwords.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.username ?? '').toLowerCase().includes(q) ||
        (p.url ?? '').toLowerCase().includes(q),
    );
  }, [passwords, search]);

  async function reveal(id: number) {
    if (revealed[id] !== undefined) return;
    setRevealingId(id);
    setError(null);
    try {
      const r = await apiFetch<{ password: HuduPasswordReveal }>(
        `/api/hudu/passwords/${id}/reveal`,
        { method: 'POST' },
      );
      setRevealed((cur) => ({ ...cur, [id]: r.password.password }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to reveal.');
    } finally {
      setRevealingId(null);
    }
  }

  function hide(id: number) {
    setRevealed((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
  }

  async function copy(id: number, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // clipboard may be unavailable on http or sandboxed contexts
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-12"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-[var(--color-header)]">
            <KeyRound className="h-4 w-4" /> Hudu passwords — {organizationName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-[var(--color-muted)] hover:text-[var(--color-header)]"
          >
            ✕
          </button>
        </div>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Every reveal is logged to the audit log with your name, the password id, and the time.
        </p>

        <div className="relative mt-3">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-muted)]" />
          <input
            type="text"
            placeholder="Search by name, username, URL…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white py-2 pl-8 pr-3 text-sm"
          />
        </div>

        {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

        <div className="mt-4 max-h-[60vh] overflow-y-auto">
          {!filtered ? (
            <p className="text-sm text-[var(--color-muted)]">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              {passwords?.length === 0
                ? 'No passwords in Hudu for this client.'
                : 'No matches for that search.'}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {filtered.map((p) => {
                const plaintext = revealed[p.id];
                const isRevealing = revealingId === p.id;
                return (
                  <li key={p.id} className="py-2.5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[var(--color-header)]">{p.name}</div>
                        <div className="text-xs text-[var(--color-muted)]">
                          {p.username ?? '(no username)'}
                          {p.url ? (
                            <>
                              {' · '}
                              <a
                                href={p.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[var(--color-t3-blue)] hover:underline"
                              >
                                {p.url}
                              </a>
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {p.username && (
                          <button
                            type="button"
                            onClick={() => void copy(p.id, p.username!)}
                            className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] bg-white px-2 py-1 text-[10px] text-[var(--color-muted)] hover:bg-[var(--color-surface)]"
                            title="Copy username"
                          >
                            <Copy className="h-3 w-3" /> user
                          </button>
                        )}
                        {plaintext === undefined ? (
                          <button
                            type="button"
                            onClick={() => void reveal(p.id)}
                            disabled={isRevealing}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[var(--color-t3-blue)]/90 disabled:opacity-60"
                          >
                            <Eye className="h-3 w-3" /> {isRevealing ? 'Revealing…' : 'Reveal'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => hide(p.id)}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                          >
                            <EyeOff className="h-3 w-3" /> Hide
                          </button>
                        )}
                      </div>
                    </div>
                    {plaintext !== undefined && (
                      <div className="mt-2 flex items-stretch gap-2">
                        <input
                          readOnly
                          value={plaintext}
                          onClick={(e) => (e.target as HTMLInputElement).select()}
                          className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 font-mono text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => void copy(p.id, plaintext)}
                          className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1.5 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                        >
                          <Copy className="h-3.5 w-3.5" /> {copiedId === p.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end border-t border-[var(--color-border)] pt-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
